import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Bell, Menu, X } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function TopBar({ certPct = 0, onOpenMobileNav }) {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const { user } = useAuth();

    useEffect(() => {
        if (!open) return;
        const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
        window.addEventListener("keydown", onKey);
        setTimeout(() => inputRef.current?.focus(), 50);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);

    useEffect(() => {
        if (!q.trim()) { setResults(null); return; }
        const t = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/search`, { params: { q } });
                setResults(data);
            } finally {
                setLoading(false);
            }
        }, 250);
        return () => clearTimeout(t);
    }, [q]);

    const go = (url) => { setOpen(false); setQ(""); navigate(url); };

    return (
        <>
            <header
                className="h-16 sticky top-0 z-30 bg-ink-50/80 backdrop-blur border-b border-ink-300 flex items-center px-4 md:px-8 gap-4"
                data-testid="topbar"
            >
                <button
                    className="lg:hidden p-2 -ml-2 text-ink-700"
                    onClick={onOpenMobileNav}
                    data-testid="mobile-nav-trigger"
                    aria-label="Open menu"
                >
                    <Menu size={20} strokeWidth={1.5} />
                </button>

                <div className="hidden lg:block">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">Certification</div>
                    <div className="flex items-center gap-3 mt-0.5">
                        <div className="w-32 h-1 bg-ink-200 rounded-full overflow-hidden">
                            <div
                                className="h-full bg-haevn-700 transition-all"
                                style={{ width: `${certPct}%` }}
                                data-testid="topbar-cert-bar"
                            />
                        </div>
                        <span className="text-xs text-ink-700 font-medium" data-testid="topbar-cert-pct">{certPct}%</span>
                    </div>
                </div>

                <div className="flex-1" />

                <button
                    onClick={() => setOpen(true)}
                    className="hidden md:flex items-center gap-2 px-3 py-2 text-sm bg-white border border-ink-300 hover:border-haevn-700 rounded-sm text-ink-500 transition-colors min-w-[280px]"
                    data-testid="topbar-search-btn"
                >
                    <Search size={14} strokeWidth={1.5} />
                    <span>Search modules, cards, resources…</span>
                    <span className="ml-auto font-mono text-[10px] text-ink-400 border border-ink-300 rounded px-1.5 py-0.5">⌘K</span>
                </button>
                <button
                    onClick={() => setOpen(true)}
                    className="md:hidden p-2 text-ink-700"
                    data-testid="topbar-search-btn-mobile"
                    aria-label="Search"
                >
                    <Search size={18} strokeWidth={1.5} />
                </button>

                <button className="p-2 text-ink-500 hover:text-ink-900 transition-colors" data-testid="topbar-notifications-btn" aria-label="Notifications">
                    <Bell size={18} strokeWidth={1.5} />
                </button>

                <div className="w-9 h-9 rounded-full bg-haevn-700 text-white flex items-center justify-center text-xs font-medium" data-testid="topbar-avatar">
                    {user?.name?.[0]?.toUpperCase() ?? "?"}
                </div>
            </header>

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-ink-900/40 backdrop-blur-sm flex items-start justify-center pt-[10vh] px-4"
                    onClick={() => setOpen(false)}
                    data-testid="search-overlay"
                >
                    <div
                        className="w-full max-w-2xl bg-white border border-ink-300 rounded-sm shadow-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center gap-3 px-5 py-4 border-b border-ink-200">
                            <Search size={18} strokeWidth={1.5} className="text-ink-500" />
                            <input
                                ref={inputRef}
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search HAEVN University…"
                                className="flex-1 bg-transparent outline-none text-base"
                                data-testid="search-input"
                            />
                            <button onClick={() => setOpen(false)} className="text-ink-400 hover:text-ink-700">
                                <X size={16} strokeWidth={1.5} />
                            </button>
                        </div>
                        <div className="max-h-[60vh] overflow-y-auto scrollbar-thin">
                            {loading && <div className="p-6 text-sm text-ink-500">Searching…</div>}
                            {!loading && results && (
                                <div className="p-2">
                                    {["modules", "flashcards", "resources", "roleplays"].map((sec) => (
                                        results[sec]?.length > 0 && (
                                            <div key={sec} className="py-2">
                                                <div className="px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-ink-400 font-mono">{sec}</div>
                                                {results[sec].map((r) => (
                                                    <button
                                                        key={r.id}
                                                        onClick={() => {
                                                            if (sec === "modules") go(`/modules/${r.id}`);
                                                            else if (sec === "flashcards") go(`/flashcards`);
                                                            else if (sec === "resources") go(`/resources`);
                                                            else go(`/roleplays`);
                                                        }}
                                                        data-testid={`search-result-${sec}-${r.id}`}
                                                        className="w-full text-left px-3 py-2 rounded-sm hover:bg-ink-100 transition-colors"
                                                    >
                                                        <div className="text-sm text-ink-900 font-medium">{r.title || r.front}</div>
                                                        {r.description && <div className="text-xs text-ink-500 truncate">{r.description}</div>}
                                                    </button>
                                                ))}
                                            </div>
                                        )
                                    ))}
                                    {results && Object.values(results).every((v) => !v?.length) && (
                                        <div className="p-6 text-sm text-ink-500" data-testid="search-empty">No matches.</div>
                                    )}
                                </div>
                            )}
                            {!q && (
                                <div className="p-6 text-sm text-ink-500 font-mono uppercase tracking-[0.15em] text-[11px]">
                                    Type to search modules, flash cards, resources, roleplays
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
